// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { FunctionComponent, PropsWithChildren, ReactElement, useState, useEffect } from "react";
import { Typography, Box, Alert } from '@mui/material';
import { useAuthContext } from '@asgardeo/auth-react';
import CustomerHealthTable from '../../components/CustomerHealthTable';
import { CustomerHealthFilters, HealthFilters } from '../../components/CustomerHealthFilters';
import { SearchCustomerHealth } from '../../components/SearchCustomerHealth';
import { AccountSummary } from '../../data/utils/types';
import { RESOURCE_SERVER_URLS } from '../../data/configs/spl-config';

interface DashboardPropsInterface {}

const DEFAULT_FILTERS: HealthFilters = {
    accountScope: "all-accounts",
    userEmail: null,
    riskIndicators: [],
    region: [],
    product: null,
    abtTeam: null,
    healthStatus: null
};

const buildExportPayload = (filters: HealthFilters, searchQuery: string, offset: number, limit: number) => ({
    offset,
    limit,
    email: (filters.accountScope === "my-accounts" && filters.userEmail) ? filters.userEmail : "",
    phrase: (searchQuery && searchQuery.length >= 2) ? searchQuery : "",
    risks: (Array.isArray(filters.riskIndicators) && filters.riskIndicators.length > 0) ? filters.riskIndicators.join(',') : "",
    region: (Array.isArray(filters.region) && filters.region.length > 0) ? filters.region : [],
    product: filters.product || "",
    abtTeam: filters.abtTeam || "",
    healthStatus: filters.healthStatus || ""
});

const CustomerHealthDashboard: FunctionComponent<PropsWithChildren<DashboardPropsInterface>> = (
): ReactElement => {

    const { getAccessToken, httpRequest } = useAuthContext();

    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const [filters, setFilters] = useState<HealthFilters>(() => {
        const saved = sessionStorage.getItem("dashboard_filters");
        if (!saved) return DEFAULT_FILTERS;
        try {
            const parsed = JSON.parse(saved);
            return { ...DEFAULT_FILTERS, ...parsed };
        } catch (e) {
            return DEFAULT_FILTERS;
        }
    });

    useEffect(() => {
        sessionStorage.setItem("dashboard_filters", JSON.stringify(filters));
    }, [filters]);

    const [page, setPage] = useState<number>(() => {
        const saved = sessionStorage.getItem("dashboard_page");
        if (!saved) return 0;
        const parsed = parseInt(saved, 10);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    });

    const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
        const saved = sessionStorage.getItem("dashboard_rows_per_page");
        if (!saved) return 10;
        const parsed = parseInt(saved, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
    });

    useEffect(() => {
        sessionStorage.setItem("dashboard_page", String(page));
    }, [page]);

    useEffect(() => {
        sessionStorage.setItem("dashboard_rows_per_page", String(rowsPerPage));
    }, [rowsPerPage]);

    const [searchQuery, setSearchQuery] = useState<string>(() => {
        return sessionStorage.getItem("dashboard_search") || "";
    });

    useEffect(() => {
        sessionStorage.setItem("dashboard_search", searchQuery);
    }, [searchQuery]);

    const handleFilterChange = (newFilters: HealthFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const handleSearch = (query: string) => {
        if (query !== searchQuery) {
            setPage(0);
        }
        setSearchQuery(query);
    };

    const handleExport = async () => {
        setExporting(true);
        setExportError(null);
        try {
            const baseUrl = RESOURCE_SERVER_URLS[0] + '/customer-health/summary';
            const token = await getAccessToken();
            const headers = {
                'x-jwt-assertion': token,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            };

            const BATCH = 20;
            const firstResponse = await httpRequest({ url: baseUrl, method: 'POST', data: buildExportPayload(filters, searchQuery, 0, BATCH), headers });
            const firstResult = firstResponse.data as { data: AccountSummary[]; totalCount: number };
            const totalCount = firstResult.totalCount;
            let allAccounts: AccountSummary[] = [...firstResult.data];

            let offset = BATCH;
            while (offset < totalCount) {
                const response = await httpRequest({ url: baseUrl, method: 'POST', data: buildExportPayload(filters, searchQuery, offset, BATCH), headers });
                const result = response.data as { data: AccountSummary[]; totalCount: number };
                allAccounts = [...allAccounts, ...result.data];
                offset += BATCH;
            }

            const statusLabel = (s?: string) =>
                !s || s === 'to_be_reviewed' ? 'To Be Reviewed' : s === 'at_risk' ? 'At Risk' : 'Healthy';

            const CSV_HEADERS = [
                'Account Name', 'Health Status',
                'Has Gone Live', 'Support Activity (Last 6 Mo)', 'Using EOL Products',
                'Abandoned Migrations', 'Migration Delays', 'Escalations (Last 3 Mo)'
            ];

            const yesNo = (v: boolean) => v ? 'Yes' : 'No';
            const rows = allAccounts.map(acc => [
                acc.accountName || acc.accountSysId,
                statusLabel(acc.healthStatus),
                (!acc.hasNoGoLive.isRisk && acc.hasNoGoLive.state === 'pending') ? 'Pending (Not Live Yet)' : yesNo(!acc.hasNoGoLive.isRisk),
                yesNo(!acc.noSupportCases6mo),
                yesNo(acc.hasEolProduct),
                yesNo(acc.hasAbandonedMigrations),
                yesNo(acc.hasMigrationDelays),
                yesNo(acc.hasRecentEscalations),
            ]);

            const sanitizeCSV = (v: string) => /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
            const csv = [CSV_HEADERS, ...rows]
                .map(row => row.map(v => `"${sanitizeCSV(String(v)).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `customer-health-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
            setExportError('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h4" gutterBottom fontWeight={'bold'}>
                Customer Health Dashboard
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                Monitor account parameters and identify risks
            </Typography>
            <SearchCustomerHealth
                onSearch={handleSearch}
                initialValue={searchQuery}
            />
            <CustomerHealthFilters
                onFilterChange={handleFilterChange}
                currentFilters={filters}
            />
            {exportError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setExportError(null)}>
                    {exportError}
                </Alert>
            )}
            <CustomerHealthTable
                accountScope={filters.accountScope}
                userEmail={filters.userEmail}
                searchQuery={searchQuery}
                riskIndicators={filters.riskIndicators}
                region={filters.region}
                product={filters.product}
                abtTeam={filters.abtTeam}
                healthStatus={filters.healthStatus}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onRowsPerPageChange={(newRows) => { setRowsPerPage(newRows); setPage(0); }}
                onExport={handleExport}
                exporting={exporting}
            />
        </Box>
    );
};

export default CustomerHealthDashboard;
